import React, { FC, useState, useEffect, useCallback } from 'react';
import { ShoppingBagOutlined } from '@mui/icons-material';
import {
  Button,
  Divider,
  Box,
  IconButton,
  Typography,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'store/hooks/useAppSelector';
import {
  selectResultTotalPrice,
  selectTotalPrice,
} from 'store/selectors/orderSelectors';
import { selectAppSettingsData } from 'store/selectors/appSelectors';
import { selectCarryOutAndDeliverySettings } from 'store/selectors/otherSelectors';
import { SnackActions } from 'components/generic';
import { useSnackbar } from 'notistack';

const BasketButton: FC = () => {
  const navigate = useNavigate();
  const { totalPrice, totalPriceKop } = useAppSelector(selectTotalPrice);
  const { delivery, carryOut } = useAppSelector(
    selectCarryOutAndDeliverySettings
  );
  const { DELIVERY_STOP_MESSAGE } = useAppSelector<{
    DELIVERY_STOP_MESSAGE: string;
  }>(selectAppSettingsData);
  const [isShowPrice, setIsShowPrice] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const { resultTotalPrice } = useAppSelector(selectResultTotalPrice);

  const handleBasketButton = useCallback(() => {
    if (!delivery && !carryOut) {
      enqueueSnackbar(DELIVERY_STOP_MESSAGE, {
        action: SnackActions,
        variant: 'warning',
        autoHideDuration: 3000,
        preventDuplicate: true,
      });
    } else {
      navigate('/cart');
    }
  }, [delivery, carryOut]);

  useEffect(() => {
    if (totalPriceKop > 0) {
      setIsShowPrice(true);
    } else {
      setIsShowPrice(false);
    }
  }, [totalPriceKop]);

  return (
    <>
      <Button
        sx={{
          display: { xs: 'none', md: 'flex' },
          borderRadius: '46px',
          textTransform: 'inherit',
          padding: '12px 16px 12px 20px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          '&:focus': {
            boxShadow: 'none',
          },
        }}
        variant="contained"
        onClick={handleBasketButton}
        size="large"
      >
        <ShoppingBagOutlined sx={{ color: 'primary.contrastText' }} />
        <Divider
          sx={{
            backgroundColor: 'primary.contrastText',
            width: '2px',
            margin: '0 16px 0 9px',
          }}
          flexItem
          variant="middle"
          orientation="vertical"
        />

        <Typography
          sx={{
            color: 'primary.contrastText',
            fontWeight: '300',
            whiteSpace: 'nowrap',
          }}
        >
          {resultTotalPrice}
        </Typography>
      </Button>
      <Box
        sx={{
          position: 'relative',
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
        }}
      >
        {isShowPrice && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              padding: '2px 4px',
              backgroundColor: 'primary.main',
              zIndex: '2',
              position: 'absolute',
              right: '20px',
              bottom: '0px',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
            onClick={handleBasketButton}
          >
            <Typography
              sx={{
                color: 'primary.contrastText',
                fontSize: '12px',
                fontWeight: '300',
                lineHeight: '13px',
              }}
            >
              {resultTotalPrice}
            </Typography>
          </Stack>
        )}

        <IconButton onClick={handleBasketButton}>
          <ShoppingBagOutlined fontSize="medium" color="primary" />
        </IconButton>
      </Box>
    </>
  );
};

export default BasketButton;
